import { notify, useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { useInvoicingApiActionSegmentEvents } from '@features/invoices/hooks/useInvoicingApiActionSegmentEvents'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { CustomerModalSubmitActionType } from '../enums/customerModalSubmitActionType'
import { Contact } from '../types/contact'
import { CustomerForm } from '../types/customerForm'
import { getAddressExemptPayloadValue } from '../utils/getAddressExemptPayloadValue'
import { getCreateCustomerPayload } from '../utils/getCreateCustomerPayload'
import { getCreateShippingAddressPayload } from '../utils/getCreateShippingAddressPayload'
import { getIsSubmitActionTypeCreate } from '../utils/getIsSubmitActionTypeCreate'
import { isShippingAddressIncluded } from '../utils/isShippingAddressIncluded'
import { useCreateAddress } from './useCreateAddress'
import { useCreateContact } from './useCreateContact'
import { useCustomerSubmissionHandlers } from './useCustomerSubmissionHandlers'
import { useUpdateAddress } from './useUpdateAddress'
import { useUpdateContact } from './useUpdateContact'

interface CustomerFormSubmitProps {
  onSubmit: (customer: Contact) => void
  submitActionType?: CustomerModalSubmitActionType
}

export const useCustomerFormSubmit = ({ onSubmit, submitActionType }: CustomerFormSubmitProps) => {
  const { t } = useTranslation()
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { sendEvent } = useInvoicingApiActionSegmentEvents()
  const { close: closeCustomerModal } = useModal(ModalId.CustomerModal)
  const isSubmitActionTypeCreate = getIsSubmitActionTypeCreate(submitActionType)
  const handleCustomerSubmit = (customer: Contact) => {
    if (isSubmitActionTypeCreate) {
      sendEvent.contact.created()
    }
    onSubmit(customer)
    closeCustomerModal()
  }
  const successMessage = isSubmitActionTypeCreate
    ? t('customer_modal.form.create.notification.success')
    : t('customer_modal.form.edit.notification.success')

  const { handleCustomerSubmitSuccess, handleCustomerSubmitError } = useCustomerSubmissionHandlers({
    onSubmit: handleCustomerSubmit,
    shouldInvalidateAddress: true,
    successMessage,
  })

  const { create: createCustomer, isProcessing: isCreatingCustomer } = useCreateContact({
    onSuccess: handleCustomerSubmitSuccess,
    onError: handleCustomerSubmitError,
  })

  const { update: updateCustomer, isProcessing: isUpdatingCustomer } = useUpdateContact({
    onSuccess: handleCustomerSubmitSuccess,
    onError: handleCustomerSubmitError,
  })

  const handleAddressSubmitError = (notificationKey: NotificationKeys) => {
    notify({ id: notificationKey, message: t('error_saving'), variant: 'error' })
  }

  const { createAsync: createAddress, isProcessing: isCreatingAddress } = useCreateAddress({
    onError: () => handleAddressSubmitError(NotificationKeys.CreateAddress),
  })

  const { updateAsync: updateAddress, isProcessing: isUpdatingAddress } = useUpdateAddress({
    onError: () => handleAddressSubmitError(NotificationKeys.UpdateAddress),
  })

  const isLoading = isOrganizationLoading
  const isSubmitting = isCreatingCustomer || isCreatingAddress || isUpdatingCustomer || isUpdatingAddress

  const submit = useCallback(
    async (customerForm: CustomerForm) => {
      const organizationId = organization?.id
      let shippingAddressId: string | undefined

      const addressExemptPayloadValue = getAddressExemptPayloadValue(customerForm, isFrenchComplianceMode)
      const isIncludedShippingAddress =
        !addressExemptPayloadValue && isShippingAddressIncluded(customerForm.shippingAddress)

      if (isIncludedShippingAddress) {
        const addressPayload = getCreateShippingAddressPayload(customerForm, organizationId)
        const saveAddress = isSubmitActionTypeCreate ? createAddress : updateAddress
        const saveAddressResponse = await saveAddress(addressPayload)
        const { addresses } = saveAddressResponse || {}
        shippingAddressId = addresses?.[0]?.id
      }

      const customerPayload = getCreateCustomerPayload(
        { ...customerForm, isAddressExempt: addressExemptPayloadValue },
        organizationId,
        shippingAddressId,
      )

      const saveCustomer = isSubmitActionTypeCreate ? createCustomer : updateCustomer
      saveCustomer(customerPayload)
    },
    [
      createAddress,
      createCustomer,
      isFrenchComplianceMode,
      isSubmitActionTypeCreate,
      organization?.id,
      updateAddress,
      updateCustomer,
    ],
  )

  return {
    isLoading,
    isSubmitting,
    submit,
  }
}
